/**
 * useWebSocketHandler — FME-specific WebSocket event dispatch.
 *
 * Handles FME-local actions that require component state:
 * - Reloading the currently visible section on compute completion
 * - Fetching compute errors for the error panel/modal
 * - Setting the computeFailed flag for the FME UI
 *
 * Cache-level operations (section invalidation, tree patching, input error
 * patching) are handled by the global useGlobalWebSocketHandler hook
 * in ModelLayoutInner, which stays alive across all model pages.
 */

import { useCallback } from 'react';
import { getComputeErrors, getSensitivityComputeErrors } from '../../../shared/api/scenarios';

interface UseWebSocketHandlerParams {
    scenarioId: number;
    currentSensitivity: any;
    setComputeFailed: (v: boolean) => void;
    setComputeErrors: (errors: any[]) => void;
    resetLoadedSections: () => void;
}

export function useWebSocketHandler({
    scenarioId,
    currentSensitivity,
    setComputeFailed,
    setComputeErrors,
    resetLoadedSections,
}: UseWebSocketHandlerParams) {
    const handleWebSocketData = useCallback((msg: any) => {
        // ── scenario_computed — fetch errors.
        //   We used to force-reload the current section here, but the compute
        //   pipeline never mutates input model_datas (it only produces outputs,
        //   pushed through dedicated WebSocket channels, and input errors come
        //   via `scenario_input_errors`). Reloading inputs mid-edit replaced the
        //   cached `field.currentValue.value` with the backend-persisted value,
        //   which re-triggered the field components' seed-from-prop effect and
        //   wiped the user's in-progress typing. `resetLoadedSections()` below
        //   still clears the in-memory tracking so the next navigation to
        //   another section fetches fresh data.
        if (msg.type === 'scenario_computed' && currentSensitivity === 'null') {
            resetLoadedSections();
            getComputeErrors(scenarioId).then(
                (errors) => setComputeErrors(errors),
                () => setComputeErrors([]),
            );
        }

        // ── sensitivity_computed — same pattern, same rationale.
        if (msg.type === 'sensitivity_computed' && currentSensitivity !== 'null' && currentSensitivity?.id == msg.sensitivity_id) {
            resetLoadedSections();
            getSensitivityComputeErrors(currentSensitivity.id).then(
                (errors) => setComputeErrors(errors),
                () => setComputeErrors([]),
            );
        }

        // ── scenario_compute_error ──
        if (msg.type === 'scenario_compute_error' && currentSensitivity === 'null') {
            setComputeFailed(true);
            getComputeErrors(scenarioId).then(
                (errors) => setComputeErrors(errors),
                () => {},
            );
        }

        // ── sensitivity_compute_error ──
        if (msg.type === 'sensitivity_compute_error' && currentSensitivity !== 'null' && currentSensitivity?.id == msg.sensitivity_id) {
            setComputeFailed(true);
            getSensitivityComputeErrors(currentSensitivity.id).then(
                (errors) => setComputeErrors(errors),
                () => {},
            );
        }

        // ── sensitivity_input_errors — update FME error state ──
        if (msg.type === 'sensitivity_input_errors' && currentSensitivity !== 'null' && currentSensitivity?.id == msg.sensitivity_id) {
            setComputeErrors((msg.errors || []).map((e: any) => ({ ...e, source: 'sensitivity' })));
            setComputeFailed(true);
        }
    }, [currentSensitivity, setComputeFailed, setComputeErrors, resetLoadedSections, scenarioId]);

    return { handleWebSocketData };
}
